import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { PostService, PostCreate } from '../../services/post.service';
import { WebsiteService, Domain } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';

interface WpPost {
  id: number;
  title: { rendered: string };
  content: { rendered: string };
  excerpt: { rendered: string };
  slug: string;
  date: string;
  status: string;
  categories: number[];
  featured_media: number;
  yoast_head_json?: {
    title?: string;
    description?: string;
    og_title?: string;
    og_description?: string;
    og_image?: { url: string }[];
  };
  _embedded?: {
    'wp:featuredmedia'?: { source_url: string }[];
  };
}

interface WpCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}

interface ImportablePost {
  wp: WpPost;
  expanded: boolean;
  importing: boolean;
  imported: boolean;
  showConfig: boolean;
  selectedDomain: string;
  selectedLanguage: string;
  selectedStatus: 'draft' | 'published';
  selectedCategoryIds: string[];
}

const PER_PAGE = 10;

@Component({
  selector: 'app-import-posts',
  standalone: true,
  imports: [CommonModule, FormsModule, NotificationComponent],
  templateUrl: './import-posts.component.html',
  styles: [`
    :host ::ng-deep .wp-content {
      color: #d1d5db;
      font-size: 0.875rem;
      line-height: 1.7;
    }
    :host ::ng-deep .wp-content h1,
    :host ::ng-deep .wp-content h2,
    :host ::ng-deep .wp-content h3,
    :host ::ng-deep .wp-content h4 {
      color: #f3f4f6;
      font-weight: 600;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
    }
    :host ::ng-deep .wp-content h1 { font-size: 1.5rem; }
    :host ::ng-deep .wp-content h2 { font-size: 1.25rem; }
    :host ::ng-deep .wp-content h3 { font-size: 1.1rem; }
    :host ::ng-deep .wp-content p {
      margin-bottom: 0.75em;
    }
    :host ::ng-deep .wp-content a {
      color: #818cf8;
      text-decoration: underline;
    }
    :host ::ng-deep .wp-content img {
      max-width: 100%;
      height: auto;
      border-radius: 0.5rem;
      margin: 1em 0;
    }
    :host ::ng-deep .wp-content ul,
    :host ::ng-deep .wp-content ol {
      padding-left: 1.5em;
      margin-bottom: 0.75em;
    }
    :host ::ng-deep .wp-content li {
      margin-bottom: 0.25em;
    }
    :host ::ng-deep .wp-content blockquote {
      border-left: 3px solid #4b5563;
      padding-left: 1em;
      margin: 1em 0;
      color: #9ca3af;
      font-style: italic;
    }
    :host ::ng-deep .wp-content figure {
      margin: 1em 0;
    }
    :host ::ng-deep .wp-content figcaption {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 0.5em;
    }
    :host ::ng-deep .wp-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
    }
    :host ::ng-deep .wp-content th,
    :host ::ng-deep .wp-content td {
      border: 1px solid #374151;
      padding: 0.5em 0.75em;
      text-align: left;
    }
    :host ::ng-deep .wp-content th {
      background: rgba(55,65,81,0.5);
      color: #e5e7eb;
      font-weight: 600;
    }
  `]
})
export class ImportPostsComponent implements OnInit {
  posts: ImportablePost[] = [];
  wpCategories: WpCategory[] = [];
  domains: Domain[] = [];
  categories: { id: string; name: string }[] = [];

  wpUrl = 'https://beheer.dutchwatertech.nl/wp-json';
  wpBaseUrl = '';
  connected = false;
  siteName = '';

  loading = false;
  connecting = false;
  currentPage = 1;
  totalPages = 1;
  totalPosts = 0;

  defaultDomain = '';
  defaultLanguage = 'nl';

  constructor(
    private http: HttpClient,
    private router: Router,
    private postService: PostService,
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    // Only load beheer-dwt domains/categories on init
    const domainsResult = await this.websiteService.getDomains();
    this.domains = domainsResult;
    this.defaultDomain = this.websiteService.getCurrentDomain() || (this.domains[0]?.name ?? '');

    if (this.defaultDomain) {
      const cats = await this.websiteService.getCategories(this.defaultDomain);
      this.categories = cats.map(c => ({ id: c.id, name: c.name }));
    }
  }

  /**
   * Normalize the user-entered URL to a WP REST API base.
   * Accepts formats like:
   *   - https://example.com
   *   - https://example.com/wp-json
   *   - https://example.com/wp-json/wp/v2
   */
  private normalizeWpUrl(url: string): string {
    let u = url.trim().replace(/\/+$/, '');
    if (u.endsWith('/wp/v2')) u = u.replace(/\/wp\/v2$/, '');
    if (!u.endsWith('/wp-json')) u += '/wp-json';
    return u;
  }

  async connectToSite() {
    if (!this.wpUrl.trim()) {
      this.notificationService.show({ message: 'Voer een WordPress URL in', type: 'error' });
      return;
    }

    this.connecting = true;
    this.connected = false;
    this.posts = [];
    this.wpCategories = [];
    this.totalPosts = 0;

    try {
      const baseJson = this.normalizeWpUrl(this.wpUrl);

      // Test connection by fetching WP root
      const root = await this.http.get<{ name?: string; namespaces?: string[] }>(
        `${baseJson}/`
      ).toPromise();

      if (!root?.namespaces?.includes('wp/v2')) {
        this.notificationService.show({ message: 'Dit lijkt geen WordPress REST API te zijn', type: 'error' });
        this.connecting = false;
        return;
      }

      this.wpBaseUrl = `${baseJson}/wp/v2`;
      this.siteName = root.name || new URL(baseJson).hostname;
      this.connected = true;

      // Load categories and first page
      await this.loadWpCategories();
      await this.loadPage(1);

      this.notificationService.show({
        message: `Verbonden met "${this.siteName}"`,
        type: 'success'
      });
    } catch (e) {
      console.error('Connection failed:', e);
      this.notificationService.show({
        message: 'Kan niet verbinden. Controleer de URL en of de REST API beschikbaar is.',
        type: 'error'
      });
    } finally {
      this.connecting = false;
    }
  }

  private async loadWpCategories() {
    try {
      const cats = await this.http.get<WpCategory[]>(
        `${this.wpBaseUrl}/categories?per_page=100`
      ).toPromise();
      this.wpCategories = cats || [];
    } catch (e) {
      console.error('Error loading WP categories:', e);
    }
  }

  async loadPage(page: number) {
    if (!this.wpBaseUrl) return;
    this.loading = true;
    try {
      const response = await this.http.get<WpPost[]>(
        `${this.wpBaseUrl}/posts?per_page=${PER_PAGE}&page=${page}&_embed`,
        { observe: 'response' }
      ).toPromise();

      if (response) {
        this.totalPosts = parseInt(response.headers.get('X-WP-Total') || '0', 10);
        this.totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1', 10);
        this.currentPage = page;

        this.posts = (response.body || []).map(wp => ({
          wp,
          expanded: false,
          importing: false,
          imported: false,
          showConfig: false,
          selectedDomain: this.defaultDomain,
          selectedLanguage: this.defaultLanguage,
          selectedStatus: 'draft' as const,
          selectedCategoryIds: []
        }));
      }
    } catch (e) {
      console.error('Error loading WP posts:', e);
      this.notificationService.show({ message: 'Fout bij laden WordPress posts', type: 'error' });
    } finally {
      this.loading = false;
    }
  }

  getWpCategoryNames(categoryIds: number[]): string {
    return categoryIds
      .map(id => this.wpCategories.find(c => c.id === id)?.name || `#${id}`)
      .join(', ');
  }

  getFeaturedImageUrl(post: WpPost): string {
    return post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '';
  }

  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  toggleExpand(item: ImportablePost) {
    item.expanded = !item.expanded;
  }

  showImportConfig(item: ImportablePost) {
    item.showConfig = true;
  }

  cancelImport(item: ImportablePost) {
    item.showConfig = false;
  }

  toggleCategory(item: ImportablePost, categoryId: string) {
    const idx = item.selectedCategoryIds.indexOf(categoryId);
    if (idx >= 0) {
      item.selectedCategoryIds.splice(idx, 1);
    } else {
      item.selectedCategoryIds.push(categoryId);
    }
  }

  async confirmImport(item: ImportablePost) {
    if (!item.selectedDomain) {
      this.notificationService.show({ message: 'Selecteer een domein', type: 'error' });
      return;
    }

    item.importing = true;
    try {
      const wp = item.wp;
      const yoast = wp.yoast_head_json;
      const featuredImage = this.getFeaturedImageUrl(wp);

      const postData: PostCreate = {
        title: this.stripHtml(wp.title.rendered),
        content: wp.content.rendered,
        slug: wp.slug,
        status: item.selectedStatus,
        language: item.selectedLanguage as any,
        domains: [item.selectedDomain],
        categoryIds: item.selectedCategoryIds,
        featuredImage: featuredImage || yoast?.og_image?.[0]?.url || '',
        metaTitle: yoast?.title || '',
        metaDescription: yoast?.description || '',
        ogTitle: yoast?.og_title || '',
        ogDescription: yoast?.og_description || '',
        ogImage: yoast?.og_image?.[0]?.url || ''
      };

      const newId = await this.postService.createPost(postData);
      // Set basePostId to own ID
      await this.postService.updatePost(newId, { basePostId: newId });

      item.imported = true;
      item.showConfig = false;
      this.notificationService.show({
        message: `"${postData.title}" geïmporteerd`,
        type: 'success'
      });
    } catch (e) {
      console.error('Import failed:', e);
      this.notificationService.show({
        message: 'Import mislukt: ' + (e instanceof Error ? e.message : 'onbekende fout'),
        type: 'error'
      });
    } finally {
      item.importing = false;
    }
  }

  async importAll() {
    const pending = this.posts.filter(p => !p.imported && !p.importing);
    for (const item of pending) {
      item.selectedDomain = this.defaultDomain;
      item.selectedLanguage = this.defaultLanguage;
      item.selectedStatus = 'draft';
      await this.confirmImport(item);
    }
  }

  goBack() {
    this.router.navigate(['/posts']);
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.loadPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.loadPage(this.currentPage + 1);
    }
  }
}
